import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, X, Route, ListChecks, Plug, AlertTriangle, Zap, Shuffle, SlidersHorizontal, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useUiPrefs } from "@/lib/uiPrefs";

const PLATFORMS = [["", "All platforms"], ["shopee", "Shopee"], ["tiktok", "TikTok Shop"]];
const ALL_SERVICES = ["SPX Instant", "Grab Express", "Lalamove Instant", "J&T Express", "SPX Express", "Ninja Van", "City-Link"];

export default function MarketplaceOrders() {
  const navigate = useNavigate();
  const [sample, setSample] = useState(false);
  const [f, setF] = useState({ platform: "", store: "", logistics_mode: "all", services: [], delivery_category: "normal", q: "", floor: "", rack: "" });
  const [applied, setApplied] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [routeData, setRouteData] = useState(null);
  const { density, setDensity, filtersOpen, setFiltersOpen } = useUiPrefs("mp-orders");
  const activeFilters = [f.platform, f.store, f.q, f.floor, f.rack].filter(Boolean).length
    + (f.logistics_mode !== "all" ? 1 : 0) + (f.delivery_category !== "normal" ? 1 : 0);

  const load = async (nextSample = sample, filt = f) => {
    setLoading(true); setSel(new Set());
    try {
      const params = new URLSearchParams({
        sample: String(nextSample), platform: filt.platform, store: filt.store,
        logistics_mode: filt.logistics_mode, services: filt.services.join(","),
        delivery_category: filt.delivery_category, q: filt.q, floor: filt.floor, rack: filt.rack,
      }).toString();
      const r = await api.get(`/marketplace/orders?${params}`);
      setData(r.data); setApplied({ ...filt, sample: nextSample });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* surface explicit connection state on mount */ }, []);

  const toggleService = (s) => { const next = { ...f, services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s] }; setF(next); load(sample, next); };
  const preset = (cat, extra = {}) => { const next = { ...f, delivery_category: cat, ...extra }; setF(next); load(sample, next); };
  const setLogisticsMode = (mode) => { const next = { ...f, logistics_mode: mode }; setF(next); load(sample, next); };
  const setPlatform = (platform) => { const next = { ...f, platform, store: "" }; setF(next); load(sample, next); };
  const setStore = (store) => { const next = { ...f, store }; setF(next); load(sample, next); };
  const toggleSel = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };

  const goMap = () => {
    const q = new URLSearchParams();
    if (f.platform) q.set("platform", f.platform);
    if (f.store && !sample) q.set("shop_id", f.store);
    navigate(`/marketplace-mappings${q.toString() ? `?${q}` : ""}`);
  };

  const previewRoute = async () => {
    if (sel.size === 0) return toast.error("Select at least one order");
    try {
      const r = await api.post("/marketplace/orders/route-preview", { order_ids: [...sel], sample: applied?.sample });
      setRouteData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const orders = data?.orders || [];
  const showOrders = !!(data && (data.sample_mode || data.connected));
  const stores = (data?.stores || []).filter((s) => !f.platform || s.platform === f.platform);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="marketplace-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
          <h1 className="text-2xl font-bold">Order Processing</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="mp-density-toggle">
            {[["comfortable", "Comfortable"], ["compact", "Compact"]].map(([v, l]) => (
              <button key={v} data-testid={`mp-density-${v}`} onClick={() => setDensity(v)}
                className={`px-2.5 h-9 text-xs ${density === v ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" data-testid="mp-map-shortcut" onClick={goMap} className="h-9">
            <Shuffle className="w-4 h-4 mr-1.5" />Map SKUs
          </Button>
          <label className="flex items-center gap-2 text-sm" data-testid="mp-sample-toggle">
            <input type="checkbox" checked={sample} onChange={(e) => { setSample(e.target.checked); load(e.target.checked); }} />
            <span className="font-medium">Sample Mode</span>
            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">isolated / non-production</Badge>
          </label>
        </div>
      </div>

      {/* connection banner */}
      {data && !data.sample_mode && !data.connected && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-start gap-3 text-sm" data-testid="mp-not-connected">
          <Plug className="w-5 h-5 text-slate-400 mt-0.5" />
          <div><div className="font-medium text-slate-700">Not Connected</div>
            <div className="text-slate-500">{data.message}</div></div>
        </div>
      )}
      {data && !data.sample_mode && data.connected && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800 flex items-center gap-2" data-testid="mp-live-banner">
          <Plug className="w-4 h-4" />{data.message}
        </div>
      )}
      {data?.sample_mode && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-center gap-2" data-testid="mp-sample-banner">
          <AlertTriangle className="w-4 h-4" />{data.message}
        </div>
      )}

      {/* filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="mp-filters-card">
        <button data-testid="mp-filters-toggle" onClick={() => setFiltersOpen(!filtersOpen)} className="flex items-center justify-between w-full text-left">
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" />Filters{activeFilters > 0 && <Badge variant="outline" className="text-[10px] text-sky-700 border-sky-300" data-testid="mp-active-filters">{activeFilters} active</Badge>}</span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
        </button>
        {filtersOpen && (<div className="space-y-3" data-testid="mp-filters-body">
        <div className="grid sm:grid-cols-5 gap-3">
          <div><label className="text-xs text-slate-500 block mb-1">Platform</label>
            <select data-testid="mp-platform" value={f.platform} onChange={(e) => setPlatform(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Store</label>
            <select data-testid="mp-store" value={f.store} onChange={(e) => setStore(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              <option value="">{f.platform ? "All stores" : "All stores"}</option>
              {stores.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
          <div className="sm:col-span-2"><label className="text-xs text-slate-500 block mb-1">Search (order ID / SKU)</label>
            <div className="flex gap-2">
              <div className="relative flex-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input data-testid="mp-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Type then press Search / Enter" className="pl-8 h-9" /></div>
            </div></div>
          <div className="flex items-end"><Button data-testid="mp-apply" onClick={() => load()} className="h-9 w-full bg-slate-900 hover:bg-slate-800"><Search className="w-4 h-4 mr-1.5" />Search</Button></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Delivery category</label>
            <div className="flex gap-1.5 flex-wrap">
              {[["normal", "Normal (excl. Instant)"], ["instant", "Instant Only"], ["all", "All"]].map(([v, l]) => (
                <button key={v} data-testid={`mp-cat-${v}`} onClick={() => preset(v)}
                  className={`px-3 h-8 rounded-md text-xs border ${f.delivery_category === v ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
              <button data-testid="mp-preset-all-except-instant" onClick={() => setF({ ...f, delivery_category: "all", logistics_mode: "exclude", services: ["SPX Instant", "Grab Express", "Lalamove Instant"] })}
                className="px-3 h-8 rounded-md text-xs border border-amber-300 bg-amber-50 text-amber-800">All except Instant Delivery</button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Logistics</label>
            <div className="flex items-center gap-2">
              <select data-testid="mp-logistics-mode" value={f.logistics_mode} onChange={(e) => setLogisticsMode(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2">
                <option value="all">All</option><option value="include">Include selected</option><option value="exclude">Exclude selected</option>
              </select>
              <span className="text-xs text-slate-400">{f.logistics_mode === "all" ? "no restriction" : "pick services below"}</span>
            </div>
          </div>
        </div>

        {f.logistics_mode !== "all" && (
          <div className="flex flex-wrap gap-1.5" data-testid="mp-services">
            {ALL_SERVICES.map((s) => (
              <button key={s} data-testid={`mp-service-${s}`} onClick={() => toggleService(s)}
                className={`px-2.5 h-7 rounded-full text-xs border flex items-center gap-1 ${f.services.includes(s) ? "bg-slate-800 text-white border-slate-800" : "bg-white border-slate-200 text-slate-600"}`}>
                {s}{f.services.includes(s) && <X className="w-3 h-3" />}
              </button>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-slate-500 block mb-1">Floor</label>
            <select data-testid="mp-floor" value={f.floor} onChange={(e) => setF({ ...f, floor: e.target.value })} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              <option value="">Any floor</option><option value="2">Floor 2 (A–Z picking)</option><option value="1">Floor 1 (near packing)</option>
            </select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Rack starts with</label>
            <Input data-testid="mp-rack" value={f.rack} onChange={(e) => setF({ ...f, rack: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="e.g. A" className="h-9" /></div>
          <div className="flex items-end gap-2">
            <Button variant="outline" data-testid="mp-reset" onClick={() => { const r = { platform: "", store: "", logistics_mode: "all", services: [], delivery_category: "normal", q: "", floor: "", rack: "" }; setF(r); load(sample, r); }} className="h-9">Reset</Button>
          </div>
        </div>
        </div>)}
      </div>

      {/* counts + actions */}
      {showOrders && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600 flex flex-wrap gap-3" data-testid="mp-counts">
            <span><b>{data.counts.orders}</b> orders</span>
            <span><b>{data.workload?.sku_work_lines}</b> SKU work lines</span>
            <span className="text-slate-400">{data.workload?.total_physical_qty} physical qty</span>
            <span><b>{sel.size}</b> selected</span>
          </div>
          <Button data-testid="mp-preview-route" onClick={previewRoute} disabled={sel.size === 0} className="bg-emerald-600 hover:bg-emerald-700 h-9"><Route className="w-4 h-4 mr-1.5" />Preview Rack Route</Button>
        </div>
      )}

      {/* orders: desktop table */}
      {loading ? <div className="p-8 text-center text-slate-400">Loading…</div> : showOrders && (
        <>
          <div className="hidden md:block rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className={`w-full text-sm min-w-[820px] ${density === "compact" ? "[&_td]:py-1 [&_th]:py-1" : ""}`}>
              <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                <th className="p-2 w-8"></th><th className="p-2 text-left">Order</th><th className="p-2 text-left">Platform / Store</th>
                <th className="p-2 text-left">Lines (SKU · Qty · Rack)</th><th className="p-2 text-left">Carrier / Service</th>
                <th className="p-2 text-left">Ship By</th><th className="p-2 text-left">Flags</th>
              </tr></thead>
              <tbody>
                {orders.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No orders match the filters</td></tr> :
                  orders.map((o) => (
                    <tr key={o.erp_id} data-testid={`mp-order-${o.erp_id}`} className={`border-t border-slate-100 ${sel.has(o.erp_id) ? "bg-sky-50/60" : ""}`}>
                      <td className="p-2"><input type="checkbox" data-testid={`mp-check-${o.erp_id}`} checked={sel.has(o.erp_id)} onChange={() => toggleSel(o.erp_id)} /></td>
                      <td className="p-2"><div className="font-mono text-xs font-semibold">{o.erp_id}</div><div className="text-[10px] text-slate-400">{o.marketplace_id}</div></td>
                      <td className="p-2 text-xs"><span className="capitalize">{o.platform}</span><div className="text-slate-400">{o.store}</div></td>
                      <td className="p-2">
                        <div className="space-y-0.5">
                          {o.lines.map((l, i) => (
                            <div key={i} className="text-xs flex items-center gap-1.5">
                              <span className="font-mono">{l.sku}</span><span className="text-slate-400">×{l.qty}</span>
                              <Badge variant="outline" className={`text-[10px] ${l.route?.in_normal_route && l.mapping_state === "mapped" ? "border-emerald-300 text-emerald-700" : "border-rose-300 text-rose-600"}`}>{l.rack}</Badge>
                              {l.mapping_state !== "mapped" && <span className="text-[10px] text-rose-600">unmapped</span>}
                              {l.route?.issue && <span className="text-[10px] text-amber-600">{l.route.issue}</span>}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="p-2 text-xs">{o.carrier}<div className="text-slate-400 flex items-center gap-1">{o.service_class === "instant" && <Zap className="w-3 h-3 text-amber-500" />}{o.service}</div></td>
                      <td className="p-2 text-xs text-slate-500">{o.ship_by}</td>
                      <td className="p-2">
                        {o.service_class === "instant" && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50 mr-1">Instant</Badge>}
                        {o.cross_area && <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300 mr-1" data-testid={`mp-crossarea-${o.erp_id}`}>Cross-area</Badge>}
                        {o.needs_review && <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300">Review</Badge>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* mobile cards */}
          <div className="md:hidden space-y-2">
            {orders.map((o) => (
              <div key={o.erp_id} data-testid={`mp-card-${o.erp_id}`} className={`rounded-lg border p-3 ${sel.has(o.erp_id) ? "border-sky-400 bg-sky-50/60" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={sel.has(o.erp_id)} onChange={() => toggleSel(o.erp_id)} /><span className="font-mono text-xs font-semibold">{o.erp_id}</span></label>
                  {o.service_class === "instant" && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">Instant</Badge>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 capitalize">{o.platform} · {o.store} · {o.service}</div>
                <div className="mt-1.5 space-y-0.5">
                  {o.lines.map((l, i) => (
                    <div key={i} className="text-xs flex items-center gap-1.5"><span className="font-mono">{l.sku}</span><span className="text-slate-400">×{l.qty}</span>
                      <Badge variant="outline" className={`text-[10px] ${l.route?.in_normal_route && l.mapping_state === "mapped" ? "border-emerald-300 text-emerald-700" : "border-rose-300 text-rose-600"}`}>{l.rack}</Badge></div>
                  ))}
                </div>
                <div className="mt-1.5 flex gap-1">{o.cross_area && <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300">Cross-area</Badge>}{o.needs_review && <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300">Review</Badge>}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* route preview dialog */}
      <Dialog open={!!routeData} onOpenChange={(v) => !v && setRouteData(null)}>
        <DialogContent className="max-w-2xl" data-testid="mp-route-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Route className="w-5 h-5" />Rack Route Preview</DialogTitle></DialogHeader>
          {routeData && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="text-xs text-slate-500">{routeData.message}</div>
              <div className="flex flex-wrap gap-3 text-sm bg-slate-50 rounded-md p-2.5" data-testid="mp-route-workload">
                <span><ListChecks className="w-4 h-4 inline text-slate-400" /> <b>{routeData.workload?.sku_work_lines}</b> work lines</span>
                <span className="text-slate-400">{routeData.workload?.total_physical_qty} physical qty</span>
                <span>Target <b>{routeData.workload?.workload_target_per_packer}</b>/packer</span>
                <span>~<b>{routeData.workload?.estimated_packers}</b> packer(s)</span>
                {routeData.workload?.overflow_waiting_lines > 0 && <span className="text-amber-700">{routeData.workload.overflow_waiting_lines} waiting</span>}
              </div>
              <div>
                <div className="text-xs font-semibold text-emerald-700 mb-1">Normal route — Floor 2 A→Z then Floor 1</div>
                <div className="rounded-md border border-slate-200 divide-y">
                  {routeData.route.length === 0 ? <div className="p-3 text-xs text-slate-400">No picking-route lines</div> :
                    routeData.route.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 text-xs" data-testid={`mp-route-row-${i}`}>
                        <span className="w-6 text-slate-300">{i + 1}</span>
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700">{r.rack}</Badge>
                        <span className="text-slate-400">F{r.floor}</span>
                        <span className="font-mono">{r.sku}</span><span className="text-slate-400">×{r.qty}</span>
                        <span className="ml-auto text-[10px] text-slate-400">{r.erp_id}</span>
                      </div>
                    ))}
                </div>
              </div>
              {routeData.excluded.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-rose-700 mb-1">Excluded / needs review (not routed through Balance Stock)</div>
                  <div className="rounded-md border border-rose-200 divide-y">
                    {routeData.excluded.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 text-xs" data-testid={`mp-route-excl-${i}`}>
                        <Badge variant="outline" className="border-rose-300 text-rose-600">{r.rack}</Badge>
                        <span className="font-mono">{r.sku}</span><span className="text-slate-400">×{r.qty}</span>
                        <span className="ml-auto text-[10px] text-amber-700">{r.exclude_reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
